import {
  ANALYTICS_ENVIRONMENTS,
  BILLING_INTERVALS,
  SHIPPING_COUNTRIES,
  normalizePostcode,
} from '@oefy/core';
import { z } from 'zod';
import { emailSchema } from './auth';
import { schoolGroupSchema, timestampSchema } from './common';

export const shopPackageSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  interval: z.enum(BILLING_INTERVALS),
  amountCents: z.int(),
  currency: z.string(),
  monthlyCents: z.int(),
  bookPackage: z.boolean(),
  trialDays: z.int().nullable(),
  firstPaymentCents: z.int(),
});

export const shopAnalyticsSchema = z.object({
  key: z.string(),
  distinctId: z.string(),
  flagKey: z.string().nullable(),
  /** Tags every browser event, because staging and production share one PostHog project. */
  environment: z.enum(ANALYTICS_ENVIRONMENTS),
});

export const shopPackagesSchema = z.object({
  packages: z.array(shopPackageSchema),
  variant: z.string().nullable(),
  analytics: shopAnalyticsSchema.nullable(),
});

const firstNameSchema = z.string().trim().min(1).max(40);

export const checkoutChildSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('new'), firstName: firstNameSchema, schoolGroup: schoolGroupSchema }),
  z.object({ kind: z.literal('existing'), childId: z.uuid() }),
  z.object({ kind: z.literal('extra_group'), childId: z.uuid(), schoolGroup: schoolGroupSchema }),
]);

const addressLine = (max: number) => z.string().trim().min(1).max(max);

export const shippingAddressSchema = z
  .object({
    name: addressLine(120),
    street: addressLine(120),
    houseNumber: addressLine(20),
    postcode: addressLine(10),
    city: addressLine(80),
    country: z.enum(SHIPPING_COUNTRIES),
  })
  .transform((address, context) => {
    const postcode = normalizePostcode(address.country, address.postcode);
    if (postcode === null) {
      context.addIssue({
        code: 'custom',
        path: ['postcode'],
        message: 'Invalid postcode',
      });
      return z.NEVER;
    }
    return { ...address, postcode };
  });

export const checkoutInputSchema = z.object({
  productId: z.uuid(),
  /** The price the parent saw. Only a guard: the API charges its own price and refuses a mismatch. */
  expectedAmountCents: z.number().int().positive(),
  child: checkoutChildSchema,
  shipping: shippingAddressSchema.optional(),
  parent: z
    .object({
      email: emailSchema,
      name: z.string().trim().max(120).optional(),
    })
    .optional(),
  acceptTerms: z.literal(true),
});

export const checkoutStartSchema = z.object({
  checkoutId: z.uuid(),
  checkoutUrl: z.url(),
});

export const checkoutStatusSchema = z.object({
  id: z.uuid(),
  status: z.enum(['open', 'completed', 'failed']),
  email: z.string(),
  productName: z.string(),
  childName: z.string().nullable(),
  schoolGroup: schoolGroupSchema,
  extraGroup: z.boolean(),
  childId: z.uuid().nullable(),
  bookPackage: z.boolean(),
  trialUntil: timestampSchema.nullable(),
});

export type ShopPackage = z.infer<typeof shopPackageSchema>;
export type ShopPackages = z.infer<typeof shopPackagesSchema>;
export type ShopAnalytics = z.infer<typeof shopAnalyticsSchema>;
export type ShippingAddressInput = z.input<typeof shippingAddressSchema>;
export type CheckoutChild = z.infer<typeof checkoutChildSchema>;
export type CheckoutInput = z.output<typeof checkoutInputSchema>;
export type CheckoutRequest = z.input<typeof checkoutInputSchema>;
export type CheckoutStart = z.infer<typeof checkoutStartSchema>;
export type CheckoutStatus = z.infer<typeof checkoutStatusSchema>;
